import { CommonModule } from '@angular/common'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { Router } from '@angular/router'
import { AuthService } from '@auth/services/auth.service'
import { NbFormFieldModule, NbInputModule } from '@nebular/theme'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { WorkspaceService } from '../../services/workspace.service'

/**
 * Searches the current list of workspaces
 */
@Component({
  selector: 'ngx-workspaces-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  imports: [CommonModule, NbFormFieldModule, MatIcon, NbInputModule]
})
export class SearchComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /** Bumps on each fetch so a slower older page cannot append after a new search */
  private requestId = 0
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /** Search term */
  term = ''

  /**
   * List of workspaces that matches the search term
   */
  @Output()
  workspacesChanged: EventEmitter<Workspace[]> = new EventEmitter<Workspace[]>()
  /**
   * Indicator to show no more workspaces
   */
  @Output()
  noMoreWorkspaces: EventEmitter<boolean> = new EventEmitter<boolean>()
  /**
   * List of available workspaces
   */
  workspaces: Workspace[] = []
  /**
   * Current page of workspaces
   */
  page = 0
  /**
   * Indicator to stop retrieving more workspaces as per there is no more
   */
  stopRetrieving = false
  /**
   * Indicator to stop request for more workspaces while retrieving some
   */
  pendingRequest = false

  /**
   * Creates the component and injects it's dependencies
   * @param workspaceService Workspace Service
   * @param authService Auth Service
   * @param router Router
   */
  constructor(private workspaceService: WorkspaceService, private authService: AuthService, private router: Router) {
    this.workspaceService.spliceWorkspace$.pipe(takeUntil(this._destroy$)).subscribe((res: number) => {
      const workspaceIndex = this.workspaces.findIndex((workspace) => workspace.id === res)
      if (workspaceIndex >= 0) this.workspaces.splice(workspaceIndex, 1)
    })
  }

  /**
   * OnInit implementation - refreshes which means it gets a list of current workspaces on start
   */
  ngOnInit() {
    this.refresh()
  }

  /**
   * Filters current available workspaces and emits the result
   * @param term search term
   */
  search = (e) => {
    this.term = e.target.value
    this.page = 0
    this.workspaces = []
    this.stopRetrieving = false
    this.pendingRequest = false
    this.noMoreWorkspaces.emit(false)
    this.refresh()
  }

  /**
   * refreshes the workspace list by retrieving it from backend and storing it
   */
  refresh() {
    if (this.pendingRequest || this.stopRetrieving) return
    this.pendingRequest = true
    const requestId = ++this.requestId
    this.workspaceService
      .getWorkspaces(null, ++this.page, this.term)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: any) => {
          if (requestId !== this.requestId) return
          const workspaces = response.data || []
          if (!workspaces.length && !this.authService.isAdmin && !this.term) this.router.navigateByUrl('/workspaces/almost-there')
          if (response.current_page >= response.last_page) {
            this.stopRetrieving = true
            this.noMoreWorkspaces.emit(true)
          }
          if (!workspaces.length) this.noMoreWorkspaces.emit(false)

          this.workspaces.push(...workspaces.filter((workspace) => workspace.id == null || !this.workspaces.some((existing) => existing.id === workspace.id)))
          this.authService.workspaces = this.workspaces
          this.workspacesChanged.emit(this.workspaces)
          this.pendingRequest = false
        },
        error: () => {
          if (requestId === this.requestId) this.pendingRequest = false
        }
      })
  }
}
